import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useVerifyInvitation, useDeclineInvitation } from '@/hooks/useInvitations';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { toast } from '@/utils/toast';
import api from '@/services/api';
import { devLog } from '@/utils/logger';

/**
 * Invitation Accept Page
 *
 * Simple flow for invited users:
 * 1. Show invitation details (team, role, inviter)
 * 2. User sets their password
 * 3. Account is activated and user is logged in
 */

export const InvitationAccept = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { loginWithResponse } = useAuth();

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard against the auto-reload wiping typed setup-form input mid-session.
  useUnsavedGuard(
    fullName.trim().length > 0 || password.length > 0 || confirmPassword.length > 0,
    'invitation-accept',
  );

  // Verify invitation token (public endpoint - no auth required)
  const {
    data: verifyData,
    isLoading: isVerifying,
    error: verifyError,
  } = useVerifyInvitation(token || '', { enabled: !!token && token.length === 64 });

  const declineMutation = useDeclineInvitation();

  // Extract invitation details
  const invitation = verifyData?.invitation;
  const teamName = verifyData?.team?.name || 'Unknown Team';
  const inviterName = invitation?.invitedBy?.fullName || 'Unknown User';
  const role = invitation?.teamRole || 'MEMBER';
  const message = invitation?.message;
  const invitedEmail = invitation?.email;

  // Check invitation status
  const isExpired = invitation?.status === 'EXPIRED';
  const isAlreadyAccepted = invitation?.status === 'ACCEPTED';
  const isDeclined = invitation?.status === 'DECLINED';
  const isInvalidToken = !token || token.length !== 64;

  /**
   * Handle form submission - set password and activate account
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (password.length < 8) {
      setError(t('invitations_error_password_min'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('invitations_error_password_mismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post(`/api/invitations/${token}/set-password`, {
        password,
        fullName: fullName.trim() || undefined,
      });

      if (response.data.success) {
        // Log in the user with the returned token
        const { accessToken, user, team } = response.data.data;

        // Use the auth context loginWithResponse
        await loginWithResponse(accessToken, {
          ...user,
          team: { id: team.id, name: team.name },
        });

        toast.success(t('invitations_setup_success'));

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/');
        }, 500);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('invitations_error_generic');
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  /**
   * Handle decline invitation
   */
  const handleDecline = async () => {
    if (!token) return;

    try {
      await declineMutation.mutateAsync(token);
      toast.info(t('invitations_decline_success'));
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (err) {
      devLog.error('Decline invitation error:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      <div className="relative bg-white rounded-lg shadow-2xl p-8 w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[45%] h-auto"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        {/* Loading State */}
        {isVerifying && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
            <p className="text-gray-600">{t('invitations_verifying')}</p>
          </div>
        )}

        {/* Invalid Token */}
        {isInvalidToken && !isVerifying && (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invitations_invalid_token_title')}</h1>
            <p className="text-gray-600 mb-6">{t('invitations_invalid_token_message')}</p>
            <Link to="/login">
              <Button variant="primary">{t('invitations_go_to_login')}</Button>
            </Link>
          </div>
        )}

        {/* Verification Error */}
        {verifyError && !isVerifying && !isInvalidToken && (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invitations_error_title')}</h1>
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {verifyError instanceof Error ? verifyError.message : t('invitations_error_generic')}
            </div>
            <Link to="/login">
              <Button variant="primary">{t('invitations_go_to_login')}</Button>
            </Link>
          </div>
        )}

        {/* Already Accepted */}
        {isAlreadyAccepted && !isVerifying && (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invitations_already_accepted_title')}</h1>
            <p className="text-gray-600 mb-6">{t('invitations_already_accepted_message')}</p>
            <Link to="/login">
              <Button variant="primary">{t('invitations_go_to_login')}</Button>
            </Link>
          </div>
        )}

        {/* Declined */}
        {isDeclined && !isVerifying && (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invitations_declined_title')}</h1>
            <p className="text-gray-600 mb-6">{t('invitations_declined_message')}</p>
            <Link to="/login">
              <Button variant="primary">{t('invitations_go_to_login')}</Button>
            </Link>
          </div>
        )}

        {/* Expired */}
        {isExpired && !isVerifying && (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invitations_expired_title')}</h1>
            <p className="text-gray-600 mb-6">{t('invitations_expired_message')}</p>
            <Link to="/login">
              <Button variant="primary">{t('invitations_go_to_login')}</Button>
            </Link>
          </div>
        )}

        {/* Valid Invitation - Show Setup Form */}
        {invitation && !isVerifying && !isExpired && !isAlreadyAccepted && !isDeclined && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
              {t('invitations_setup_title')}
            </h1>
            <p className="text-center text-gray-600 mb-6">
              {t('invitations_setup_subtitle')}
            </p>

            {/* Invitation Details Card */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <Icon name={ICONS.TEAM} size="sm" color="text-primary-600" ariaHidden />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{teamName}</p>
                  <p className="text-sm text-gray-500">
                    {t('invitations_invited_by_label')}: {inviterName}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 rounded bg-primary-100 text-primary-700 font-medium capitalize">
                  {t(`role_${role.toLowerCase()}`, { defaultValue: role.toLowerCase() })}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-600">{invitedEmail}</span>
              </div>

              {message && (
                <p className="mt-3 text-sm text-gray-600 italic border-t border-gray-200 pt-3">
                  "{message}"
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Password Setup Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name (optional, pre-fill with email prefix) */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('invitations_field_fullname')}
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={invitedEmail?.split('@')[0]}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('invitations_field_password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-2.5 pr-10 rounded-lg border border-gray-400 focus:border-primary-400 focus:outline-none"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name={showPassword ? ICONS.EYE_SLASH : ICONS.EYE} size="sm" ariaHidden />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">{t('invitations_password_hint')}</p>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('invitations_field_confirm_password')} <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-400 focus:border-primary-400 focus:outline-none"
                  placeholder="••••••••"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isSubmitting}
                disabled={isSubmitting || !password || !confirmPassword}
                className="w-full"
              >
                {t('invitations_setup_button')}
              </Button>
            </form>

            {/* Decline Link */}
            <div className="mt-4 text-center">
              <button
                onClick={handleDecline}
                disabled={declineMutation.isPending}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                {t('invitations_decline_link')}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <p className="text-xs text-gray-500 text-center mb-2">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            © {new Date().getFullYear()} Made with <span className="text-primary-500">❤️</span> by{' '}
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <a href="https://overw8.de" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">
              OVERW8
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
